import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SpeedSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
}

export default function SpeedSlider({
  value,
  onChange,
  disabled = false,
  min = 1.0,
  max = 2.5,
  step = 0.01,
  defaultValue = 1.72
}: SpeedSliderProps) {
  const { t } = useTranslation('common_ui');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="p-1.5 bg-bg-elevated hover:bg-border-default rounded-lg text-gray-400 hover:text-gray-300 transition-colors disabled:opacity-50 text-xs border border-border-default"
        title={t('speedSlider.title')}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg p-3 z-50 min-w-[200px]">
          <div className="text-xs text-gray-400 mb-2 flex justify-between">
            <span>{t('speedSlider.label')}</span>
            <span className="text-purple-400">{value.toFixed(2)}</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
              disabled={disabled}
              className="w-full h-1 bg-border-default rounded-lg appearance-none cursor-pointer accent-purple-500"
              style={{ direction: 'rtl' }}
            />
            {/* 默认值标记 */}
            <div
              className="absolute top-0 h-1 w-0.5 bg-yellow-500"
              style={{ left: `${((max - defaultValue) / (max - min)) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{t('speedSlider.slow')}</span>
            <span>{t('speedSlider.fast')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
